/**
 * Full-page user editor (/users/:userId) — reached by double-clicking a row on
 * /users. Replaces the old inline dialog so there is ONE editing surface.
 *
 * Profile fields are edited through the same allow-listed PATCH the list used.
 * Everything under "Identity" is READ-ONLY: it lives in the separate
 * hiyve-identity database, outside the profiles write API that every editable
 * field here goes through. In particular `role` (teacher/student/owner/studio)
 * is the product role and is deliberately not settable from this console.
 *
 * Each panel loads independently so the page renders as soon as the profile
 * arrives — the identity/activity lookups cross into another database and must
 * never hold up the part the operator actually came to edit.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuItem from '@mui/material/MenuItem';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';

const PROTECTED_ROLES = ['force-ok', 'admin', 'skip-charge'];

const ROLE_SUGGESTIONS = [
  'tenant-owner', 'org-owner', 'org-admin', 'org-developer', 'org-user',
  'user-manager', 'public-files', 'private-files', 'hiyve-user', 'hiyve-console',
];

const TEXT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'organization', label: 'Organization' },
  { key: 'brandName', label: 'Brand name' },
  { key: 'picture', label: 'Picture URL' },
];

const TIER_LABEL = { pro: 'Pro', standard: 'Standard', studio: 'Studio', legacy: 'Legacy' };
const STATUS_LABEL = { active: 'paying', trialing: 'free trial', canceled: 'canceled' };

/** teacher/student/owner/studio — the product role. */
const IDENTITY_ROLE_COLOR = {
  teacher: 'primary',
  student: 'info',
  owner: 'secondary',
  studio: 'warning',
};

const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '—');

function Panel({ title, children, action }) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {action}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Paper>
  );
}

function Field({ label, value, mono }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
      <Typography
        variant="body2"
        sx={{ wordBreak: 'break-all', ...(mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}) }}
      >
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </Typography>
    </Stack>
  );
}

/**
 * One metadata key: label, current value, and a lock toggle.
 *
 * Locked renders as static text. The lock is UI friction only — the server
 * type-checks every write against the same registry, because a direct API call
 * never sees this component.
 */
function MetadataRow({ fieldKey, field, value, locked, onToggleLock, onChange, error }) {
  const hardLocked = Boolean(field.locked);

  const control = () => {
    if (field.options) {
      return (
        <TextField
          select fullWidth size="small" value={value ?? ''}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        >
          <MenuItem value=""><em>none</em></MenuItem>
          {field.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    }
    if (field.type === 'boolean') {
      return (
        <Switch
          checked={value === true}
          onChange={(e) => onChange(fieldKey, e.target.checked)}
        />
      );
    }
    if (field.type === 'string[]') {
      return (
        <Autocomplete
          multiple freeSolo size="small" options={[]}
          value={Array.isArray(value) ? value : []}
          onChange={(_e, v) => onChange(fieldKey, v)}
          renderInput={(params) => <TextField {...params} placeholder="add…" />}
        />
      );
    }
    if (field.type === 'object') {
      return (
        <TextField
          fullWidth size="small" multiline minRows={2}
          value={typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          error={Boolean(error)} helperText={error || 'JSON object'}
        />
      );
    }
    return (
      <TextField
        fullWidth size="small" value={value ?? ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
      />
    );
  };

  const readable = value === undefined || value === null || value === ''
    ? '—'
    : typeof value === 'object' ? JSON.stringify(value) : String(value);

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.75 }}>
      <Box sx={{ minWidth: 190, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">{field.label}</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
          {fieldKey}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        {locked
          ? <Typography variant="body2" sx={{ pt: 1, wordBreak: 'break-all' }}>{readable}</Typography>
          : control()}
        {field.help && !locked && (
          <Typography variant="caption" color="text.secondary">{field.help}</Typography>
        )}
      </Box>
      <Tooltip title={hardLocked ? (field.help || 'Managed elsewhere — not editable here') : locked ? 'Unlock to edit' : 'Lock'}>
        <span>
          <IconButton
            size="small" disabled={hardLocked}
            onClick={() => onToggleLock(fieldKey)}
            color={!locked && !hardLocked ? 'warning' : 'default'}
          >
            {locked || hardLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function toForm(user) {
  return {
    name: user.name || '',
    phone: user.phone || '',
    organization: user.organization || '',
    brandName: user.brandName || '',
    picture: user.picture || '',
    roles: user.roles || [],
    active: user.active !== false,
    deleted: user.deleted === true,
  };
}

function diffOf(form, original) {
  const updates = {};
  for (const { key } of TEXT_FIELDS) {
    if ((form[key] ?? '') !== (original[key] ?? '')) updates[key] = form[key] ?? '';
  }
  if (form.active !== original.active) updates.active = form.active;
  if (form.deleted !== original.deleted) updates.deleted = form.deleted;
  const before = [...(original.roles || [])].sort().join(',');
  const after = [...(form.roles || [])].sort().join(',');
  if (before !== after) updates.roles = form.roles;
  return updates;
}

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  // Metadata edits live outside `form`: each key is independently locked, and
  // only unlocked+changed keys are sent.
  const [meta, setMeta] = useState({});
  const [unlocked, setUnlocked] = useState(() => new Set());
  const [metaErrors, setMetaErrors] = useState({});

  // Auto-dismiss the saved banner with a cleaned-up timer (a bare setTimeout
  // would fire after unmount).
  useEffect(() => {
    if (!saved) return undefined;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['tenant-user', userId],
    queryFn: () => api.get(`/api/tenant-users/${userId}`),
  });

  const user = data?.user;
  const identity = data?.identity;

  // Drop any in-progress form when the route points at a different user.
  // React Router reuses this component across /users/a -> /users/b, so without
  // this the previous user's values would persist while `user` is the new one —
  // and saving would write A's values onto B.
  useEffect(() => {
    setForm(null);
    setError(null);
    setMeta({});
    setUnlocked(new Set());
    setMetaErrors({});
  }, [userId]);

  // Seed once the profile lands. Guarded on `form` being null so a background
  // refetch never stomps what the operator is typing.
  useEffect(() => {
    if (user && !form) {
      setForm(toForm(user));
      setMeta(user.metadata ?? {});
    }
  }, [user, form]);

  const sessionsQ = useQuery({
    queryKey: ['tenant-user-sessions', userId],
    queryFn: () => api.get(`/api/tenant-users/${userId}/sessions`),
    enabled: Boolean(user),
  });
  const activityQ = useQuery({
    queryKey: ['tenant-user-activity', userId],
    queryFn: () => api.get(`/api/tenant-users/${userId}/activity`),
    enabled: Boolean(user),
  });
  // Rendered from the SERVER's registry so the two cannot disagree about a
  // field's type or lock state.
  const fieldsQ = useQuery({
    queryKey: ['tenant-user-fields'],
    queryFn: () => api.get('/api/tenant-users/meta/fields'),
    staleTime: 1000 * 60 * 30,
  });

  const auditQ = useQuery({
    queryKey: ['tenant-user-audit', userId],
    queryFn: () => api.get(`/api/tenant-users/${userId}/audit`),
    enabled: Boolean(user),
  });

  const updateMutation = useMutation({
    mutationFn: (updates) => api.patch(`/api/tenant-users/${userId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-user-audit', userId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      setSaved(true);
    },
    onError: (err) => setError(err?.message || 'Update failed'),
  });

  const offeredProtected = (form?.roles || []).filter((r) => PROTECTED_ROLES.includes(r));

  /** Parse an object-typed field, which the editor holds as raw JSON text. */
  function coerce(key, field, value) {
    if (field.type !== 'object') return { value };
    if (typeof value !== 'string') return { value };
    try {
      const parsed = JSON.parse(value || '{}');
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { error: 'must be a JSON object' };
      }
      return { value: parsed };
    } catch {
      return { error: 'not valid JSON' };
    }
  }

  function handleSave() {
    setError(null);
    const fields = fieldsQ.data?.fields ?? {};
    const original = user?.metadata ?? {};

    // ONLY unlocked keys are considered — that is what makes the padlock mean
    // something. Omitted keys are left alone by the server (partial patch), so
    // unknown keys we never render are safe.
    const metadataPatch = {};
    const errs = {};
    for (const key of unlocked) {
      const field = fields[key];
      if (!field || field.locked) continue;
      const { value, error: err } = coerce(key, field, meta[key]);
      if (err) { errs[key] = err; continue; }
      if (JSON.stringify(value ?? null) !== JSON.stringify(original[key] ?? null)) {
        metadataPatch[key] = value === '' ? null : value;
      }
    }
    setMetaErrors(errs);
    if (Object.keys(errs).length) return;

    const updates = diffOf(form, user);
    delete updates.metadata; // superseded by the per-field patch
    if (Object.keys(metadataPatch).length) updates.metadata = metadataPatch;
    if (!Object.keys(updates).length) return;
    updateMutation.mutate(updates);
  }

  const toggleLock = (key) => setUnlocked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (isError) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>Back to users</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{loadError?.message || 'User not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Tooltip title="Back to users">
          <IconButton onClick={() => navigate('/users')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h5">{user?.name || user?.email}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {identity?.role && (
          <Chip
            label={identity.role}
            color={IDENTITY_ROLE_COLOR[identity.role] || 'default'}
            sx={{ textTransform: 'capitalize' }}
          />
        )}
        {user?.deleted && <Chip label="Deleted" color="error" variant="outlined" />}
        {!user?.deleted && !user?.active && <Chip label="Inactive" color="warning" variant="outlined" />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}

      <Grid container spacing={2}>
        {/* ── Editable profile ─────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Panel title="Profile">
            {user?.isOwner && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Organization owner — roles cannot be changed and the account cannot be deleted.
              </Alert>
            )}
            {form && (
              <Stack spacing={2}>
                <TextField
                  label="Email" value={user.email} size="small" disabled
                  helperText="Immutable — an email change is an identity-side operation"
                />
                {TEXT_FIELDS.map(({ key, label }) => (
                  <TextField
                    key={key} label={label} size="small" value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                ))}
                <Autocomplete
                  multiple freeSolo size="small" options={ROLE_SUGGESTIONS}
                  value={form.roles} disabled={user.isOwner}
                  onChange={(_e, v) => setForm((f) => ({ ...f, roles: v }))}
                  renderInput={(params) => (
                    <TextField
                      {...params} label="Platform roles"
                      error={offeredProtected.length > 0}
                      helperText={
                        offeredProtected.length
                          ? `${offeredProtected.join(', ')} cannot be granted here — platform-level roles are set out-of-band`
                          : 'Capabilities in this console. Not the same as the identity role above.'
                      }
                    />
                  )}
                />
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
                    label="Active"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.deleted} disabled={user.isOwner} onChange={(e) => setForm((f) => ({ ...f, deleted: e.target.checked }))} />}
                    label="Deleted"
                  />
                </Stack>
                <Box>
                  <Button
                    variant="contained" onClick={handleSave}
                    disabled={updateMutation.isPending || offeredProtected.length > 0}
                  >
                    {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Panel>
        </Grid>

        {/* ── Subscription (read-only — owned by muzie.live) ────────── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Panel title="Subscription">
            {!data?.user?.subscription?.applicable ? (
              <Typography variant="body2" color="text.secondary">
                Billing does not apply to students.
              </Typography>
            ) : (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  {data.user.subscription.tier
                    ? (
                      <Chip
                        label={
                          data.user.subscription.status
                            ? `${TIER_LABEL[data.user.subscription.tier] || data.user.subscription.tier} · ${STATUS_LABEL[data.user.subscription.status] || data.user.subscription.status}`
                            : (TIER_LABEL[data.user.subscription.tier] || data.user.subscription.tier)
                        }
                        color={data.user.subscription.status === 'active' ? 'success' : 'default'}
                      />
                    )
                    : <Chip label="No plan" variant="outlined" />}
                </Stack>
                <Field label="Plan id" value={data.user.subscription.planId} />
                <Field label="Status" value={data.user.subscription.status} />
                <Field label="On free trial" value={data.user.subscription.onFreeTrial ? 'yes' : 'no'} />
                <Field label="Trial ends" value={data.user.subscription.trialEndsAt ? fmt(data.user.subscription.trialEndsAt) : null} />
                <Field
                  label="Period ends"
                  value={data.user.subscription.currentPeriodEnd
                    ? fmt(new Date(data.user.subscription.currentPeriodEnd * 1000))
                    : null}
                />
                <Field label="Cancels at period end" value={data.user.subscription.cancelAtPeriodEnd ? 'yes' : 'no'} />
                {data?.billing && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Field label="Stripe customer" value={data.billing.stripeCustomerId} mono />
                    <Field label="Subscription id" value={data.billing.subscriptionId} mono />
                    <Field label="Mode" value={data.billing.mode} />
                  </>
                )}
                {data.user.subscription.tier === 'legacy' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Migrated from muzie.studio and not on a live plan. Whether they
                    still pay on the legacy Stripe account cannot be determined here.
                  </Alert>
                )}
                <Alert severity="info" sx={{ mt: 2 }}>
                  Read-only. Billing is owned by muzie.live.
                </Alert>
              </>
            )}
          </Panel>
        </Grid>

        {/* ── Identity (read-only) ─────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Panel title="Identity">
            {!identity ? (
              <Alert severity="warning">
                No identity record linked to this profile.
              </Alert>
            ) : (
              <>
                {data?.roleMismatch && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Role disagreement: the identity system says
                    {' '}<strong>{identity.role}</strong>, this tenant&apos;s profile says
                    {' '}<strong>{user?.role}</strong>. The list filters on the profile value.
                  </Alert>
                )}
                {identity.brandOrgMismatch && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This profile is linked to an identity account in a different
                    brand org ({identity.brandOrgId}). Sessions and devices are
                    withheld because they would be another org&apos;s data.
                  </Alert>
                )}
                <Field label="Role" value={identity.role} />
                <Field label="Email verified" value={identity.emailVerified === null ? null : String(identity.emailVerified)} />
                <Field label="Created" value={identity.createdAt ? fmt(identity.createdAt) : null} />
                <Field label="Identity user id" value={identity.identityUserId} mono />
                <Field label="Clean user id" value={identity.cleanUserId} mono />
                <Field label="Brand org" value={identity.brandOrgId} mono />
                {Object.keys(identity.metadata || {}).length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary">Identity metadata</Typography>
                    {Object.entries(identity.metadata).map(([k, v]) => (
                      <Field key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : v} />
                    ))}
                  </>
                )}
                <Alert severity="info" sx={{ mt: 2 }}>
                  Read-only. These live in the identity system, not this console.
                </Alert>
              </>
            )}
          </Panel>
        </Grid>

        {/* ── Teacher codes / relationships ────────────────────────── */}
        <Grid size={12}>
          <Panel title="Teacher codes & relationships">
            {!data?.relationships ? (
              <Typography variant="body2" color="text.secondary">No relationships.</Typography>
            ) : (
              <Stack spacing={2}>
                {(data.relationships.ownTeacherCode || data.relationships.ownStudioId) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Own codes</Typography>
                    {data.relationships.ownTeacherCode && (
                      <Field label="Teacher code" value={data.relationships.ownTeacherCode} mono />
                    )}
                    {data.relationships.ownStudioId && (
                      <Field label="Studio id" value={data.relationships.ownStudioId} mono />
                    )}
                  </Box>
                )}

                {data.relationships.teachers.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Teachers ({data.relationships.teachers.length})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {data.relationships.teachers.map((t) => (
                          <TableRow key={t.code}>
                            <TableCell sx={{ fontFamily: 'monospace', width: 120 }}>{t.code}</TableCell>
                            <TableCell>
                              {t.resolved
                                ? (t.name ? `${t.name} · ${t.email}` : t.email)
                                : <Typography variant="body2" color="text.disabled">
                                    not in this tenant
                                  </Typography>}
                            </TableCell>
                            <TableCell sx={{ width: 100 }}>
                              {t.status && (
                                <Chip size="small" label={t.status}
                                  color={t.status === 'active' ? 'success' : 'default'} variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ width: 60 }}>
                              {t.resolved && (
                                <Tooltip title="Open this teacher">
                                  <IconButton size="small" onClick={() => navigate(`/users/${t._id}`)}>
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {data.relationships.studios.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Studios ({data.relationships.studios.length})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {data.relationships.studios.map((st) => (
                          <TableRow key={st.code}>
                            <TableCell sx={{ fontFamily: 'monospace', width: 120 }}>{st.code}</TableCell>
                            <TableCell>{st.resolved ? (st.name || st.email) : '—'}</TableCell>
                            <TableCell sx={{ width: 100 }}>
                              {st.status && <Chip size="small" label={st.status} variant="outlined" />}
                            </TableCell>
                            <TableCell align="right" sx={{ width: 60 }}>
                              {st.resolved && (
                                <IconButton size="small" onClick={() => navigate(`/users/${st._id}`)}>
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {!data.relationships.ownTeacherCode
                  && !data.relationships.teachers.length
                  && !data.relationships.studios.length && (
                  <Typography variant="body2" color="text.secondary">
                    No teacher or studio links on this profile.
                  </Typography>
                )}
              </Stack>
            )}
          </Panel>
        </Grid>

        {/* ── Metadata: typed fields, each individually lockable ────── */}
        <Grid size={12}>
          <Panel title="Metadata">
            {fieldsQ.isLoading ? <CircularProgress size={22} /> : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Fields are locked by default. Unlock only what you intend to change —
                  the save sends nothing else. Values are type-checked on the server.
                </Alert>
                {(fieldsQ.data?.groups ?? []).map((group) => {
                  // Every registry field in this group, whether the profile
                  // carries it or not. Filtering to present-only made absent
                  // fields unreachable: unlocking needs a rendered row, so a
                  // teacher with no teacherCode could never be given one —
                  // which is most of the point of the relationships group.
                  const keys = Object.entries(fieldsQ.data.fields)
                    .filter(([, f]) => f.group === group.key);
                  if (!keys.length) return null;
                  return (
                    <Box key={group.key} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{group.label}</Typography>
                      <Divider sx={{ mb: 1 }} />
                      {keys.map(([key, field]) => (
                        <MetadataRow
                          key={key}
                          fieldKey={key}
                          field={field}
                          value={meta[key]}
                          locked={!unlocked.has(key)}
                          error={metaErrors[key]}
                          onToggleLock={toggleLock}
                          onChange={(k, v) => setMeta((m) => ({ ...m, [k]: v }))}
                        />
                      ))}
                    </Box>
                  );
                })}

                {/* Keys the registry doesn't know about: shown so nothing is
                    invisible, read-only so nothing is changed by accident. */}
                {(() => {
                  const known = new Set(Object.keys(fieldsQ.data?.fields ?? {}));
                  const unknown = Object.keys(meta).filter((k) => !known.has(k));
                  if (!unknown.length) return null;
                  return (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          Advanced — {unknown.length} key(s) with no typed field
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary">
                          Read-only here. Add a typed field in
                          server/src/routes/metadataFields.js to make one editable.
                        </Typography>
                        <TextField
                          fullWidth size="small" multiline minRows={3} sx={{ mt: 1 }}
                          value={JSON.stringify(
                            Object.fromEntries(unknown.map((k) => [k, meta[k]])), null, 2,
                          )}
                          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </>
            )}
          </Panel>
        </Grid>

        {/* ── Activity ─────────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel title="Activity & usage">
            {activityQ.isLoading ? <CircularProgress size={22} /> : !activityQ.data?.available ? (
              <Typography variant="body2" color="text.secondary">
                No activity linked to this user.
              </Typography>
            ) : (
              <>
                <Field label="Room joins" value={activityQ.data.joins} />
                <Field label="Last active" value={fmt(activityQ.data.lastActive)} />
                <Field
                  label="Recordings"
                  value={activityQ.data.recordings
                    ? `${activityQ.data.recordings.count}${activityQ.data.recordings.partial ? '+' : ''}`
                    : null}
                />
                <Field
                  label="Free minutes"
                  value={activityQ.data.freeMinutes
                    ? `${activityQ.data.freeMinutes.usedThisMonth ?? 0} / ${activityQ.data.freeMinutes.totalAllocation ?? 0}`
                    : null}
                />
                {activityQ.data.partial && (
                  <Typography variant="caption" color="text.secondary">
                    Room list truncated — counts are a floor, not a total.
                  </Typography>
                )}
                {activityQ.data.rooms?.length > 0 && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow><TableCell>Room</TableCell><TableCell>Joined</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {activityQ.data.rooms.slice(0, 8).map((r, i) => (
                        <TableRow key={`${r.roomName}-${i}`}>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{r.roomName}</TableCell>
                          <TableCell>{fmt(r.timeJoined)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </Panel>
        </Grid>

        {/* ── Sessions & devices ───────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel title="Sessions & trusted devices">
            {sessionsQ.isLoading ? <CircularProgress size={22} /> : !sessionsQ.data?.available ? (
              <Typography variant="body2" color="text.secondary">
                {sessionsQ.data?.reason === 'brand-org-mismatch'
                  ? 'Withheld — the linked identity account belongs to a different brand org.'
                  : 'No identity account linked, so no sessions to show.'}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  {sessionsQ.data.sessions.length} session(s), {sessionsQ.data.devices.length} trusted device(s)
                </Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Device</TableCell><TableCell>IP</TableCell><TableCell>Last used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessionsQ.data.devices.slice(0, 6).map((d) => (
                      <TableRow key={d._id}>
                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.deviceName || d.userAgent || '—'}
                        </TableCell>
                        <TableCell>{d.ipAddress || '—'}</TableCell>
                        <TableCell>{fmt(d.lastUsedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Panel>
        </Grid>

        {/* ── Audit ────────────────────────────────────────────────── */}
        <Grid size={12}>
          <Panel title="Audit history">
            {auditQ.isLoading ? <CircularProgress size={22} /> : !auditQ.data?.entries?.length ? (
              <Typography variant="body2" color="text.secondary">
                Nothing has been changed about this user from this console yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell><TableCell>Action</TableCell>
                    <TableCell>Changed</TableCell><TableCell>Severity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditQ.data.entries.map((e) => (
                    <TableRow key={e._id}>
                      <TableCell>{fmt(e.createdAt)}</TableCell>
                      <TableCell>{e.action}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {e.changes?.after ? Object.keys(e.changes.after).join(', ') : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small" label={e.severity}
                          color={e.severity === 'warning' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UserDetail;
